import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ContractItem } from '@core/models/contract-item.model';
import { ContractThemaNego } from '@core/models/contract-thema-nego.model';
import { Contract } from '@core/models/contract.model';
import { TvChannel } from '@core/models/tv-channel.model';
import { ContractThemaNegoService } from '@core/services/contract-thema-nego/contract-thema-nego.service';
import { ThemaGuaranteedManagingDialogComponent } from '@shared/contract/contract-item/contract-thema-nego/thema-guaranteed-managing-dialog/thema-guaranteed-managing-dialog.component';
import { ThemaListPackDialogComponent } from '@shared/contract/contract-item/contract-thema-nego/thema-list-pack-dialog/thema-list-pack-dialog.component';
import { ThemaNotGuaranteedManagingDialogComponent } from '@shared/contract/contract-item/contract-thema-nego/thema-not-guaranteed-managing-dialog/thema-not-guaranteed-managing-dialog.component';
import { Observable, Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';
@Component({
  standalone: false,
  selector: 'app-contract-thema-nego',
  templateUrl: './contract-thema-nego.component.html',
  styleUrls: ['./contract-thema-nego.component.scss'],
})
export class ContractThemaNegoComponent implements OnInit, OnDestroy {
  @Input() contract: Contract;
  @Input() contractItem: ContractItem;
  @Input() themaNegotiationList: ContractThemaNego[][];

  private filter = {};
  public isContractThemaNegoLoading = false;
  public isSavingContractThemaNego = false;
  readonly conditionColumns = ['category', 'name', 'number'];
  public itemsFilterForm: FormGroup;
  public channels$: Observable<TvChannel[]>;

  private componentDestroyed$: Subject<void> = new Subject();

  get isContractThemaNegoBusy(): boolean {
    return this.isSavingContractThemaNego || this.isContractThemaNegoLoading;
  }

  public conditionCategoryCount(conditions: ContractThemaNego['conditions']): number {
    return new Set((conditions ?? []).map(condition => condition.categoryType?.id).filter(id => id != null)).size;
  }

  constructor(
    public dialog: MatDialog,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private contractThemaNegoService: ContractThemaNegoService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.getContractThemaNegoList();
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next();
    this.componentDestroyed$.unsubscribe();
  }

  public initForm(): void {
    this.itemsFilterForm = this.fb.group({
      startDate: [null, [Validators.required]],
      endDate: [null, [Validators.required]],
      search: [null, [Validators.required]],
    });
  }

  private getContractThemaNegoList() {
    this.isContractThemaNegoLoading = true;
    this.filter = Object.assign(this.filter, {
      contractItemId: this.contractItem ? this.contractItem.id : '',
    });

    this.contractThemaNegoService
      .getList(this.filter)
      .pipe(
        take(1),
        takeUntil(this.componentDestroyed$),
        finalize(() => {
          this.isContractThemaNegoLoading = false;
          this.isSavingContractThemaNego = false;
        })
      )
      .subscribe(
        contractThemaNegoList => {
          this.themaNegotiationList = contractThemaNegoList;
        },
        () => {}
      );
  }

  public openThemaNegoModal(isGuarantee = null, themaNegoItem = null, isDuplication = false): void {
    let component = null;
    if (isGuarantee === 'guarantee') {
      component = ThemaGuaranteedManagingDialogComponent;
    } else {
      component = ThemaNotGuaranteedManagingDialogComponent;
    }

    const dialogRef = this.dialog.open(component, {
      autoFocus: false,
      data: {
        contract: this.contract,
        contractItem: this.contractItem,
        themaNegotiationData: themaNegoItem,
        isDuplication: isDuplication,
        isGuarantee: isGuarantee,
      },
      width: '1000px',
      panelClass: 'tv-dialog-container',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.['action'] === 'reload') {
        this.isSavingContractThemaNego = true;
        this.getContractThemaNegoList();
      }
    });
  }

  public openThemaPackModal(): void {
    this.dialog.open(ThemaListPackDialogComponent, {
      autoFocus: false,
      data: {
        contract: this.contract,
        contractItem: this.contractItem,
      },
      panelClass: 'tv-dialog-container',
    });
  }

  public removeThemaNegotiation(themaNegotiationItem = null): void {
    this.isSavingContractThemaNego = true;
    this.contractThemaNegoService
      .remove(themaNegotiationItem.id.toString())
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        () => {
          this.snackBar.open('Suppression de la négociation.', null, {
            duration: 4000,
            verticalPosition: 'top',
            panelClass: ['tv-chip-success'],
          });
          this.getContractThemaNegoList();
        },
        () => {
          this.isSavingContractThemaNego = false;
          this.snackBar.open('Une erreur est survenue.', null, {
            duration: 4000,
            verticalPosition: 'top',
            panelClass: ['tv-chip-error'],
          });
        }
      );
  }
}
